import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DropdownSetterContext, DropdownStateContext } from './context';

export type DropdownProps = {
  children: ReactNode;
};
export function Dropdown({ children }: DropdownProps) {
  const [triggerLayout, setTriggerLayout] = useState({ pageX: 0, width: 0, pageY: 0, height: 0 });
  const [isExpanded, setIsExpanded] = useState(false);
  const triggerFocusRestoreRef = useRef<(() => void) | null>(null);
  const prevIsExpandedRef = useRef(false);
  const restoreTriggerFocus = useCallback(() => {
    triggerFocusRestoreRef.current?.();
  }, []);

  useEffect(() => {
    const wasExpanded = prevIsExpandedRef.current;
    prevIsExpandedRef.current = isExpanded;

    if (!wasExpanded || isExpanded) {
      return undefined;
    }

    let cancelled = false;
    let innerFrameId: number | null = null;

    const outerFrameId = requestAnimationFrame(() => {
      if (cancelled) {
        return;
      }
      innerFrameId = requestAnimationFrame(() => {
        innerFrameId = null;
        if (cancelled) {
          return;
        }
        triggerFocusRestoreRef.current?.();
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(outerFrameId);
      if (innerFrameId !== null) {
        cancelAnimationFrame(innerFrameId);
      }
    };
  }, [isExpanded]);

  const stateValue = useMemo(
    () => ({
      triggerLayout,
      isExpanded,
    }),
    [triggerLayout, isExpanded]
  );

  const setTriggerFocusRestore = useMemo(
    () => (focusTrigger: (() => void) | null) => {
      triggerFocusRestoreRef.current = focusTrigger;
    },
    []
  );

  const setterValue = useMemo(
    () => ({
      setTriggerLayout,
      setIsExpanded,
      setTriggerFocusRestore,
      restoreTriggerFocus,
    }),
    [setTriggerLayout, setIsExpanded, setTriggerFocusRestore, restoreTriggerFocus]
  );

  return (
    <DropdownStateContext.Provider value={stateValue}>
      <DropdownSetterContext.Provider value={setterValue}>
        {children}
      </DropdownSetterContext.Provider>
    </DropdownStateContext.Provider>
  );
}
